import * as React from 'react';
import {useState} from 'react';
import {fetchQuery, graphql, useMutation, useRelayEnvironment} from 'react-relay';
import {AlertTriangle, CheckCircle, RefreshCw, ScanLine} from 'lucide-react';
import {AssetReturnLookupQuery} from './__generated__/AssetReturnLookupQuery.graphql';
import {AssetReturnReportMutation} from './__generated__/AssetReturnReportMutation.graphql';

const LOOKUP = graphql`
    query AssetReturnLookupQuery($assetTag: AssetTag!) {
        lostAsset(assetTag: $assetTag) {
            assetTag
            name
            category
            image
            reportedLost
        }
    }`;

const REPORT = graphql`
    mutation AssetReturnReportMutation($input: ReportFoundAssetInput!) {
        reportFoundAsset(input: $input) {
            foundReport { id }
            userErrors { message }
        }
    }`;

type Found = NonNullable<AssetReturnLookupQuery['response']['lostAsset']>;

// Public "found one of our items?" page. The tag field takes a barcode scan (tag + Enter)
// or typed input; a match shows the item and a short form to arrange its return.
const AssetReturn: React.FC = () => {
    const environment = useRelayEnvironment();
    const [tagInput, setTagInput] = useState('');
    const [status, setStatus] = useState<'idle' | 'searching' | 'found' | 'not-found'>('idle');
    const [found, setFound] = useState<Found | null>(null);
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState(false);
    const [report, reporting] = useMutation<AssetReturnReportMutation>(REPORT);

    const search = async (e: React.FormEvent) => {
        e.preventDefault();
        const tag = tagInput.trim().toUpperCase();
        if (!tag) return;
        setStatus('searching');
        setSent(false);
        setError(null);
        try {
            const result = await fetchQuery<AssetReturnLookupQuery>(environment, LOOKUP, {assetTag: tag}).toPromise();
            if (result?.lostAsset) { setFound(result.lostAsset); setStatus('found'); } else { setFound(null); setStatus('not-found'); }
        } catch (err) {
            setStatus('not-found');
            setError((err as Error).message);
        }
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!found) return;
        setError(null);
        report({
            variables: {input: {assetTag: found.assetTag, email: email.trim(), message: message.trim() || null}},
            onCompleted: (res) => {
                const payload = res.reportFoundAsset;
                if (payload?.foundReport) setSent(true);
                else setError(payload?.userErrors.map((u) => u.message).join('; ') ?? 'Something went wrong');
            },
            onError: (err) => setError(err.message),
        });
    };

    return (
        <section id="asset-return" className="py-24 bg-lumina-dark border-t border-gray-900">
            <div className="max-w-3xl mx-auto px-6">
                <div className="text-center mb-10">
                    <span className="text-red-400 font-display text-sm tracking-wider uppercase">Lost &amp; found</span>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-white mt-2">FOUND OUR <span className="text-gray-600">GEAR?</span></h2>
                    <p className="text-gray-400 mt-4">Every piece we own carries an <span className="font-mono text-gray-200">LWM-</span> tag with a barcode. Scan it or type it in and we'll arrange to get it back — and say thank you properly.</p>
                </div>

                <form onSubmit={search} className="flex flex-col md:flex-row gap-3 mb-8">
                    <label className="relative flex-1">
                        <ScanLine className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"/>
                        <input
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            placeholder="Scan or type the tag, e.g. LWM-CAM-000004"
                            autoComplete="off"
                            spellCheck={false}
                            className="w-full bg-black border border-gray-800 pl-11 pr-4 py-3 font-mono text-sm text-white focus:border-lumina-accent outline-none"
                        />
                    </label>
                    <button type="submit" disabled={status === 'searching'}
                            className="px-6 py-3 bg-white text-black font-bold text-sm tracking-widest uppercase hover:bg-lumina-accent disabled:opacity-50 flex items-center justify-center gap-2">
                        {status === 'searching' ? <RefreshCw className="w-4 h-4 animate-spin"/> : null} Look up
                    </button>
                </form>

                {status === 'not-found' && (
                    <div className="border border-gray-800 bg-black/40 p-6 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5"/>
                        <div>
                            <p className="text-white font-semibold">That tag isn't one of ours.</p>
                            <p className="text-gray-400 text-sm">Check for a leading <span className="font-mono">LWM-</span> and six digits. If the label is damaged, <a href="/contact" className="text-lumina-accent">contact us</a> with a photo.</p>
                            {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
                        </div>
                    </div>
                )}

                {status === 'found' && found && (
                    <div className="border border-gray-800 bg-black/40 p-6 grid md:grid-cols-[160px_1fr] gap-6">
                        <div className="aspect-square bg-gray-900 flex items-center justify-center overflow-hidden">
                            {found.image
                                ? <img src={found.image} alt={found.name} className="w-full h-full object-cover"/>
                                : <span className="font-mono text-[10px] text-gray-600 tracking-widest">{found.assetTag}</span>}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] font-bold px-2 py-0.5 border border-gray-700 text-gray-400 uppercase tracking-wider">{found.category.replace(/_/g, ' ')}</span>
                                {found.reportedLost && <span className="text-[10px] font-bold px-2 py-0.5 border border-red-500/40 text-red-400 uppercase tracking-wider">Reported missing</span>}
                            </div>
                            <h3 className="text-xl font-display font-bold text-white">{found.name}</h3>
                            <p className="font-mono text-xs text-lumina-accent mb-4">{found.assetTag}</p>

                            {sent ? (
                                <div className="flex items-start gap-2 text-emerald-400">
                                    <CheckCircle className="w-5 h-5 shrink-0"/>
                                    <p className="text-sm">Thank you. We've got your note and will be in touch at <span className="text-white">{email}</span> to arrange the return.</p>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-3">
                                    <p className="text-gray-400 text-sm">Yes, this is ours. Leave an email and we'll sort out pickup or a drop-off.</p>
                                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                                           className="w-full bg-black border border-gray-800 px-3 py-2 text-sm text-white focus:border-lumina-accent outline-none"/>
                                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="Where it is, when you're around, anything useful"
                                              className="w-full bg-black border border-gray-800 px-3 py-2 text-sm text-white focus:border-lumina-accent outline-none"/>
                                    {error && <p className="text-red-400 text-xs">{error}</p>}
                                    <button type="submit" disabled={reporting}
                                            className="px-6 py-3 bg-lumina-accent text-black font-bold text-sm tracking-widest uppercase hover:bg-white disabled:opacity-50">
                                        I have this item
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default AssetReturn;
